/**
 * The value sets of `RoleBoard.accent`, `RoleBoard.name` and `RoleBoard.position`.
 *
 * A board is a VISUAL GROUPING of the systems inside one Role — a bucket the
 * user creates, names, recolours, reorders and deletes. It carries no permission
 * meaning and no execution meaning, and nothing in the access path, the runtime
 * or the coverage engine reads it.
 *
 * THE DATABASE CONSTRAINS THE SHAPE, THIS FILE CONSTRAINS THE MEMBERSHIP. That
 * is the same split `RoleGroupGrant.surface` and `RoleGroupSurface.surface`
 * already make, and it is deliberate in both directions:
 *
 *  - `RoleBoard_accent_shape` (`accent ~ '^[a-z][a-z0-9_]*$'`) is permanent. It
 *    refuses garbage forever, in every environment, with no migration needed to
 *    keep doing so.
 *  - {@link ROLE_BOARD_ACCENTS} is today's palette. It grows by editing this
 *    array — no migration, and no DB enum, from which a value could never
 *    afterwards be removed (`ALTER TYPE ... ADD VALUE` has no inverse).
 *
 * So a retired-but-well-formed token like `sage_v1` PASSES the database CHECK
 * and is still rejected by {@link RoleBoardAccentSchema} on the write path.
 * Verified against a PostgreSQL 15.18 lab: the insert succeeds at the database
 * and the schema refuses it. That disagreement is the design, not a gap — but it
 * is also why every path that turns stored text into an accent must go through
 * {@link isRoleBoardAccent} rather than casting. A row written before a token
 * was retired will still hold it.
 */
import { z } from "zod";

import { ROLE_RESOURCE_TYPES } from "./role.types";

/**
 * WHAT CAN BE A CARD — EIGHT KINDS, AND THE SIX THAT CANNOT ARE THE POINT.
 *
 * The Overview screen paints all thirteen `RoleSystemNodeType` values as cards.
 * Only EIGHT of them have a row anywhere that can hold a `boardId`: the five
 * `RoleResourceType` values through `RoleResource`, plus `collection`,
 * `workspace` and `external_tool` through `RoleCollectionGrant` /
 * `RoleWorkspaceGrant` / `RoleExternalToolGrant`. `document`, `credential`,
 * `inbox_conversation`, `workflow_execution` and
 * `emulator_session` have nowhere to store a placement at all.
 *
 * So this set REFUSES those five at the API boundary, with a 400. The alternative
 * is a 200 for a move that silently did not persist — the exact class of lie the
 * Overview screen exists to remove, and the reason this API was written instead
 * of local component state.
 *
 * DERIVED from `ROLE_RESOURCE_TYPES` rather than spelled out, so a sixth
 * ownable resource type becomes placeable by construction. The three extras are
 * literals because they are not resource TYPES at all — they name the three
 * grant tables, which exist precisely because a Collection, a Workspace and an
 * external Tool are each shared across Roles and so cannot live in
 * `RoleResource`'s single-owner shape.
 *
 * ⚠️ `external_tool` MOVED between those two halves on 2026-08-13 and this set
 * did not change by one member. It was a `RoleResourceType` and is now a grant,
 * so it left the spread and arrived as a literal. That is exactly what happened
 * to `collection` before it, and it is the reason this set is the WRONG guard
 * for a `RoleResource` row: membership here says a card can be PLACED, never
 * which table the placement is written to.
 *
 * It lives HERE and not in `zroles.ts` because it is a domain value set: the
 * backend narrows a Prisma row against it and the frontend keys its lanes on it,
 * and `@nexus/types/server` re-exports `shared/domain` while it does not
 * re-export `api/domains`.
 */
export const ROLE_BOARD_CARD_TYPES = [
  ...ROLE_RESOURCE_TYPES,
  "collection",
  "workspace",
  "external_tool"
] as const;

export type RoleBoardCardType = (typeof ROLE_BOARD_CARD_TYPES)[number];

export const RoleBoardCardTypeSchema = z.enum(ROLE_BOARD_CARD_TYPES);

/**
 * 🔴 NOT FOR A ROW READ OUT OF `RoleResource`. USE `isRoleResourceType` THERE.
 *
 * This set is the five ownable kinds PLUS `collection`, `workspace` and
 * `external_tool`, which reach a card through the GRANT tables and are never
 * legitimate `RoleResource` rows. `collection` was a `RoleResourceType` until
 * its rows migrated to `RoleCollectionGrant`, and those retired rows did not
 * disappear — so narrowing a `RoleResource` row with THIS guard accepts exactly
 * the rows that must be dropped. The card then renders and fails every move,
 * because the move path writes the grant table where the stale row has no
 * counterpart, and a collection holding both a stale row and a live grant
 * double-renders. It shipped that way once;
 * `role-board-card.mapper.spec.ts` is the regression and it is mutation-proved
 * against precisely this substitution.
 *
 * `external_tool` joined that retired population on 2026-08-13 and is the
 * SAFE instance of it: it was measured at zero `RoleResource` rows on both
 * remotes before the union narrowed, because its only write path was strict-org
 * while every tool worth holding is in the shared catalogue. So the double-render
 * has no live instance for tools today — which is a fact about the data, not a
 * property of this guard, and is precisely why the guard still refuses it.
 *
 * ⚠️ The same hazard has a second spelling: `RoleBoardCardTypeSchema` is this
 * same set in Zod, so a `safeParse` against a stored `resourceType` is the
 * identical mistake. The set is right for the API BOUNDARY and wrong for a
 * `RoleResource` row, whichever way you spell it.
 */
export const isRoleBoardCardType = (value: unknown): value is RoleBoardCardType =>
  typeof value === "string" && (ROLE_BOARD_CARD_TYPES as readonly string[]).includes(value);

/**
 * The palette a board's colour is chosen from.
 *
 * Lowercase snake tokens, never hex and never a Tailwind class: this value
 * crosses the API and lands in a database column, so it has to outlive whatever
 * the frontend currently renders it as. The mapping from token to actual colour
 * belongs in the frontend, in a `Record<RoleBoardAccent, string>` so that adding
 * a token here fails the frontend's typecheck until someone gives it a colour.
 *
 * Growing this list is free and needs no migration. Removing one is not: rows
 * may already hold it, so a removal needs a data migration to repoint them.
 *
 * TWO KINDS OF TOKEN LIVE HERE, AND THE `surface_` PREFIX IS THE DIVIDER.
 *
 *  - A HUE (`indigo`, `rose`, …) names a colour. It stays recognisably itself
 *    in both themes, so naming it after the colour is honest.
 *  - A SURFACE (`surface_*`) names a POSITION IN THE NEUTRAL RAMP, not a
 *    colour. The board paints itself with the theme's own background at that
 *    depth, so the rendered colour INVERTS between light and dark.
 *
 * That inversion is why these three are named for their role and never for
 * what they look like today. Traced through the token tiers and read at the
 * leaves (`@agent-nexus/tokens` dist, 2026-08-10):
 *
 *   surface_base      → --nx-surface-bg-base          light #fcfcfd → dark neutral-2
 *   surface_secondary → --nx-surface-bg-secondary     light #f1f2f3 → dark #222324
 *   surface_contrast  → --nx-surface-bg-contrast-base light #1e1f23 → dark #eaedf2
 *
 * `surface_contrast` is the case that settles it: it is nearly BLACK in light
 * mode and nearly WHITE in dark. A token spelled `surface_dark` would name the
 * wrong end of its own ramp for every user in dark mode, and `RoleBoard.accent`
 * is read back by {@link isRoleBoardAccent} forever — a stored token cannot be
 * re-interpreted later without the data migration this list exists to avoid.
 *
 * The picker's swatch LABELS ("White", "Grey", "Dark") stay in the frontend,
 * where they are display copy and translatable. A label may describe the light
 * theme; a persisted value may not.
 */
export const ROLE_BOARD_HUE_ACCENTS = [
  "slate",
  "indigo",
  "violet",
  "sky",
  "teal",
  "emerald",
  "amber",
  "rose"
] as const;

/**
 * The neutral surfaces, in ramp order: page background, control, inverse.
 *
 * 🔴 NOT INTERCHANGEABLE WITH A HUE, AND THE COMPILER CANNOT SAY SO. Any caller
 * that picks an accent to DISTINGUISH things — a rotation over a list, a legend,
 * a chart series — must draw from {@link ROLE_BOARD_HUE_ACCENTS} and not from
 * {@link ROLE_BOARD_ACCENTS}. A surface token is the background the thing is
 * already sitting on, so a rotation that reaches one paints an item invisible
 * and typechecks perfectly.
 *
 * That is why the two families are exported separately rather than as one array
 * a caller filters by hand: a filter has to be remembered, and the member added
 * later is the one nobody re-reads the filter for.
 */
export const ROLE_BOARD_SURFACE_ACCENTS = [
  "surface_base",
  "surface_secondary",
  "surface_contrast"
] as const;

export const ROLE_BOARD_ACCENTS = [
  ...ROLE_BOARD_HUE_ACCENTS,
  ...ROLE_BOARD_SURFACE_ACCENTS
] as const;

export type RoleBoardAccent = (typeof ROLE_BOARD_ACCENTS)[number];

export const RoleBoardAccentSchema = z.enum(ROLE_BOARD_ACCENTS);

/**
 * The accent a board gets when the caller does not choose one.
 *
 * It is spelled ONCE, here, and deliberately NOT as a column DEFAULT in the
 * migration. A DB default would put the palette's identity in two places that
 * drift silently — the database would keep answering with a token this file had
 * stopped listing, and nothing would disagree.
 */
export const ROLE_BOARD_DEFAULT_ACCENT: RoleBoardAccent = "slate";

/**
 * Runtime membership check for a stored `RoleBoard.accent`.
 *
 * A type guard rather than a cast: the column is TEXT and its CHECK constrains
 * shape only, so a row that predates (or sidesteps) the write path can hold any
 * well-formed token.
 */
export const isRoleBoardAccent = (value: unknown): value is RoleBoardAccent =>
  typeof value === "string" && (ROLE_BOARD_ACCENTS as readonly string[]).includes(value);

/**
 * The longest a board name may be. The same bound `RoleBoard_name_shape`
 * enforces in the database — stated in both places on purpose, so that a caller
 * bypassing this schema still cannot write a row the UI cannot render. If the two
 * ever drift, the database refuses and the application raises, which is loud.
 */
export const ROLE_BOARD_NAME_MAX_LENGTH = 80;

/**
 * `.trim()` runs BEFORE the length checks, which is what makes this agree with
 * the database's `char_length(btrim("name")) BETWEEN 1 AND 80`. Without it a name
 * of four spaces passes `.min(1)` here and is refused by Postgres — the schema
 * would be validating something subtly different from what is stored.
 */
export const RoleBoardNameSchema = z
  .string()
  .trim()
  .min(1, "A board needs a name")
  .max(
    ROLE_BOARD_NAME_MAX_LENGTH,
    `A board name is at most ${ROLE_BOARD_NAME_MAX_LENGTH} characters`
  );

/**
 * Ordinal within the Role. Non-negative, matching `RoleBoard_position_natural`.
 *
 * Reordering renumbers the whole list from 0 rather than nudging one row, which
 * is why positions are NOT unique per Role in the database: a set-rewrite would
 * collide with itself mid-statement against a unique index. Read order is
 * (position, createdAt, id) — total, so a duplicate is untidy rather than
 * ambiguous.
 */
export const RoleBoardPositionSchema = z.number().int().nonnegative();
